import { getAnalytics } from "@pandas/analytics";
import { calculateLocale } from "@pandas/core-ui";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SecurePinPopup from "@/components/common/Popups/SecurePinPopup";
import { servicesOrchestratorValues } from "@/config/services";
import { platformApi } from "@/services";
import { useStore } from "@/store";
import { getErrorMessage } from "@/utils/getErrorMessage";

export function useCreateSession(hasSecurePin = false, onSuccess?: () => void) {
	const { i18n } = useTranslation();
	const analytics = getAnalytics();

	const [loading, setLoading] = useState(false);
	const [showSecurePinPopup, setShowSecurePinPopup] = useState(false);
	const [invalidPin, setInvalidPin] = useState(false);

	const instanceInfo = useStore((s) => s.instanceInfo);
	const partnerCode = useStore((s) => s.instanceInfo?.partner.code);
	const sessionId = useStore((s) => s.sessionId);

	const setSessionId = useStore((s) => s.setSessionId);
	const setError = useStore((s) => s.setError);

	const pendingPinRef = useRef<{
		promise: Promise<string>;
		resolve: (id: string) => void;
		reject: (err: unknown) => void;
	} | null>(null);

	const ensurePinDeferred = useCallback(() => {
		if (!pendingPinRef.current) {
			let resolve!: (id: string) => void;
			let reject!: (err: unknown) => void;
			const promise = new Promise<string>((res, rej) => {
				resolve = res;
				reject = rej;
			});
			pendingPinRef.current = { promise, resolve, reject };
		}
		return pendingPinRef.current;
	}, []);

	const clearPinDeferred = useCallback(() => {
		pendingPinRef.current = null;
	}, []);

	const getSessionId = useCallback(
		async (pin?: string): Promise<string> => {
			if (sessionId) {
				onSuccess?.();
				return sessionId;
			}
			if (!instanceInfo) throw new Error("MISSING_INSTANCE_INFO");

			if (hasSecurePin && !pin) {
				setShowSecurePinPopup(true);
				return ensurePinDeferred().promise;
			}

			setLoading(true);
			try {
				analytics.startSessionRecording();

				const data = {
					initiator: "vision_ai" as const,
					partnerId: instanceInfo.partner.id,
					locale: calculateLocale(i18n.language, undefined),
					locationId: instanceInfo.location?.id,
					securePin: pin,
				};

				const sessionResponse = await platformApi.createSession(data);
				setSessionId(sessionResponse.id);

				analytics.register({
					sessionId: sessionResponse.id,
					locationId: instanceInfo.location?.id?.toString(),
					partnerCode,
					env: servicesOrchestratorValues.app.env,
				});

				setInvalidPin(false);
				setShowSecurePinPopup(false);

				onSuccess?.();

				if (pendingPinRef.current) {
					pendingPinRef.current.resolve(sessionResponse.id);
					clearPinDeferred();
				}

				return sessionResponse.id;
			} catch (e) {
				const errorMessage = getErrorMessage(e);
				if (errorMessage === "INVALID_SECURE_PIN") {
					setInvalidPin(true);
					return ensurePinDeferred().promise;
				}
				setError(e);
				setShowSecurePinPopup(false);
				if (pendingPinRef.current) {
					pendingPinRef.current.reject(e);
					clearPinDeferred();
				}
				throw new Error("SESSION_CREATION_FAILED");
			} finally {
				setLoading(false);
			}
		},
		[
			clearPinDeferred,
			ensurePinDeferred,
			hasSecurePin,
			i18n.language,
			instanceInfo,
			onSuccess,
			partnerCode,
			analytics,
			sessionId,
			setError,
			setSessionId,
		],
	);

	const closePinPopup = useCallback(() => {
		setShowSecurePinPopup(false);
		setInvalidPin(false);
	}, []);

	const securePinElement = (
		<SecurePinPopup
			showModal={showSecurePinPopup}
			setShowModal={closePinPopup}
			loader={loading}
			invalid={invalidPin}
			setInvalid={setInvalidPin}
			onComplete={getSessionId}
		/>
	);

	return {
		loading,
		securePinElement,
		getSessionId,
		invalidPin,
		setInvalidPin,
	};
}
